import { Gym } from '#types/gyms';
import { Schedule } from '#types/schedule';
import { atom, selector } from 'recoil';

export type Period = {
  startAt?: Date;
  endAt?: Date;
};

export type SameState = {
  days: number[];
  periods: Period[];
};

export const isSameStateAtom = atom<boolean>({
  key: 'is_same_state',
  default: false
});

export const sameStateAtom = atom<SameState>({
  key: 'same_state',
  default: {
    days: [],
    periods: [
      {
        startAt: undefined,
        endAt: undefined
      }
    ]
  }
});

const formatDateTime = (date: Date) =>
  `${date.getHours().toString().padStart(2, '0')}:${date
    .getMinutes()
    .toString()
    .padStart(2, '0')}:00`;

export const sameSchedulesSelector = selector<Schedule[]>({
  key: 'same_schedules',
  get: ({ get }) => {
    const sameState = get(sameStateAtom);
    const days = sameState.days;

    return sameState.periods
      .filter(e => e.startAt && e.endAt)
      .map(period => ({
        day: days.join(','),
        startAt: formatDateTime(period.startAt!),
        endAt: formatDateTime(period.endAt!)
      }));
  }
});

export type DifferentState = Record<Day, Period[]>;

export type Day = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export const differentStateAtom = atom<DifferentState>({
  key: 'different_state',
  default: {
    0: [
      {
        startAt: undefined,
        endAt: undefined
      }
    ],
    1: [
      {
        startAt: undefined,
        endAt: undefined
      }
    ],
    2: [
      {
        startAt: undefined,
        endAt: undefined
      }
    ],
    3: [
      {
        startAt: undefined,
        endAt: undefined
      }
    ],
    4: [
      {
        startAt: undefined,
        endAt: undefined
      }
    ],
    5: [
      {
        startAt: undefined,
        endAt: undefined
      }
    ],
    6: [
      {
        startAt: undefined,
        endAt: undefined
      }
    ]
  }
});

export const differentSchedulesSelector = selector<Schedule[]>({
  key: 'different_schedules',
  get: ({ get }) => {
    const differentState = get(differentStateAtom);

    return Object.entries(differentState).reduce((acc, [key, periods]) => {
      return [
        ...acc,
        ...periods
          .filter(e => e.startAt && e.endAt)
          .map(period => ({
            day: key,
            startAt: formatDateTime(period.startAt!),
            endAt: formatDateTime(period.endAt!)
          }))
      ];
    }, [] as Schedule[]);
  }
});

export const gymStateAtom = atom<Gym | null>({
  key: 'gym_state',
  default: null
});
